import { type ComponentType } from "react";

import { cn } from "../../lib/utils";
import { BarChartBackdrop } from "./BarChartBackdrop";
import { LineChartBackdrop } from "./LineChartBackdrop";
import { TableBackdrop } from "./TableBackdrop";

export type EmptyStateBackdropVariant = "bar-chart" | "line-chart" | "table";

// Background token feathering to transparent, so content stays legible over whatever sits behind.
export const emptyStateFadeClassName =
  "bg-[radial-gradient(ellipse_at_center,hsl(var(--background))_0%,hsl(var(--background))_35%,transparent_75%)]";

const VARIANTS: Record<EmptyStateBackdropVariant, ComponentType> = {
  "bar-chart": BarChartBackdrop,
  "line-chart": LineChartBackdrop,
  table: TableBackdrop,
};

export interface EmptyStateBackdropProps {
  /**
   * The built-in, decorative placeholder visual (faded behind the content) previewing what will
   * fill this space. Hidden from assistive tech. The `EmptyState` root still needs a size from the
   * call site (e.g. `className="h-[300px] w-full"`); no wrapper or `absolute inset-0` is required.
   */
  variant: EmptyStateBackdropVariant;
}

// Painted at -z-10 inside the root's isolated stacking context: above the root's own background,
// below its in-flow content.
export const EmptyStateBackdrop = ({ variant }: EmptyStateBackdropProps) => {
  const Visual = VARIANTS[variant];
  return (
    <div
      data-slot="empty-state-backdrop"
      data-variant={variant}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10"
    >
      <Visual />
      <div className={cn("absolute inset-0", emptyStateFadeClassName)} />
    </div>
  );
};
